import { i18n } from '@lingui/core';
import { t } from '@lingui/macro';
import * as yup from 'yup';

import {
  AccessibilityIconType,
  ContentSchemaField,
  FieldType,
  PageBlockStyles,
  RouterType,
  SubPages,
  TypeParameter,
  type SocialNetworkType,
} from '@/components/common/Providers/GraphqlProvider/generated';

import { REGEX_NUMBER, REGEX_PASSWORD, REGEX_URL } from '../constants';

export const seoMetadataInputFormSchema = yup.object({
  metaTitle: yup.string().required('Campo requerido').min(1, 'Mínimo 1 carácter'),
  metaDescription: yup.string().required('Campo requerido').min(1, 'Mínimo 1 caracteres'),
  // metaKeywords: yup.array().of(yup.string()).required('Campo requerido'),
  slug: yup.string().required('Campo requerido'),
});

export type seoMetadataInputFormSchemaType = yup.InferType<typeof seoMetadataInputFormSchema>;

export const imageType = yup
  .array()
  .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' }))
  .min(1, 'Debe cargar una imagen')
  .of(
    yup
      .object()
      .shape({ id: yup.string().required() })
      .required('Imagen requerida')
      .typeError('Documento inválido')
  )
  .typeError('Documento inválido');

export const UserInfoSchema = yup.object({
  firstName: yup
    .string()
    .required(i18n._({ id: 'admin.user.form.name.requerido', message: 'Campo requerido' })),
  lastName: yup
    .string()
    .required(i18n._({ id: 'admin.user.form.lastname.requerido', message: 'Campo requerido' })),
  email: yup
    .string()
    .required(i18n._({ id: 'admin.user.form.email.requerido', message: 'Campo requerido' })),
  phoneNumber: yup
    .string()
    .required(i18n._({ id: 'admin.user.form.phone.number.requerido', message: 'Campo requerido' })),
});

export type UserInfoSchemaType = yup.InferType<typeof UserInfoSchema>;

export const VerificationSmsSchema = yup.object({
  phoneNumber: yup.string().required(
    i18n._({
      id: 'admin.user.form.verification.phone.number.requerido',
      message: 'Teléfono requerido',
    })
  ),
  verificationCode: yup.string().required(
    i18n._({
      id: 'admin.user.form.verification.field.number.requerido',
      message: 'Campo requerido',
    })
  ),
  verificationType: yup.string(),
});

export type VerificationSmsSchemaType = yup.InferType<typeof VerificationSmsSchema>;

export const VerificationEmailSchema = yup.object({
  email: yup
    .string()
    .email('Correo no válido')
    .required(
      i18n._({
        id: 'admin.user.form.verification.email.number.requerido.2',
        message: 'Correo requerido',
      })
    ),
  verificationCode: yup.string().required('Código requerido'),
});

export type VerificationEmailSchemaType = yup.InferType<typeof VerificationEmailSchema>;

export const ActionURLSchema = yup.object({
  routeType: yup
    .mixed<RouterType>()
    .nullable()
    .oneOf(Object.values(RouterType))
    .default(RouterType.NoAction)
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  url: yup
    .string()
    .nullable()
    .when('routeType', {
      is: (routeType: RouterType) =>
        routeType === RouterType.InternaltRoute || routeType === RouterType.NoAction,
      then: (schema) => schema.optional().nullable(),
      otherwise: (schema) =>
        schema
          .required(i18n._({ id: 'commons.form.required.field', message: 'Campo requerido' }))
          .when('routeType', {
            is: RouterType.ExternalRoute, // Utilizar la constante directamente
            then: (schema) => schema.matches(REGEX_URL, 'Debe ingresar una URL válida'),
          })
          .when('routeType', {
            is: RouterType.InternalRouteWithArguments, // Utilizar la constante directamente
            then: (schema) => schema.optional().nullable(),
          }),
    }),
  target: yup
    .string()
    .nullable()
    .oneOf(['_blank', '_self'])
    .when('routeType', {
      is: (routeType: RouterType) => routeType === RouterType.NoAction,
      then: (schema) => schema.optional().nullable(),
      otherwise: (schema) =>
        schema.required(
          i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })
        ),
    }),
  arguments: yup.array(yup.string()).optional().nullable(),
  routeNavigation: yup
    .string()
    .nullable()
    .default(SubPages.Transparency)
    .when('routeType', {
      is: (routeType: RouterType) => routeType === RouterType.InternaltRoute,
      then: (schema) =>
        schema.required(
          i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })
        ),
      otherwise: (schema) => schema.optional(),
    }),
});

export type ActionURLSchemaType = yup.InferType<typeof ActionURLSchema>;

export const ActionURLSchemaTramit = yup.object({
  routeTypeTramit: yup
    .mixed<RouterType>()
    .nullable()
    .oneOf(Object.values(RouterType))
    .default(RouterType.ExternalRoute)
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  urlTramit: yup
    .string()
    .nullable()
    .when('routeTypeTramit', {
      is: (routeTypeTramit: RouterType) => routeTypeTramit === RouterType.InternaltRoute,
      then: (schema) => schema.optional().nullable(),
      otherwise: (schema) =>
        schema
          .required(i18n._({ id: 'commons.form.required.field', message: 'Campo requerido' }))
          .when('routeTypeTramit', {
            is: RouterType.ExternalRoute, // Utilizar la constante directamente
            then: (schema) => schema.matches(REGEX_URL, 'Debe ingresar una URL válida'),
          })
          .when('routeTypeTramit', {
            is: RouterType.InternalRouteWithArguments, // Utilizar la constante directamente
            then: (schema) => schema.optional().nullable(),
          }),
    }),
  targetTramit: yup
    .string()
    .nullable()
    .oneOf(['_blank', '_self'])
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  argumentsTramit: yup.array(yup.string()).optional().nullable(),
  routeNavigationTramit: yup
    .string()
    .nullable()
    .default(SubPages.Transparency)
    .when('routeTypeTramit', {
      is: (routeTypeTramit: RouterType) => routeTypeTramit === RouterType.InternaltRoute,
      then: (schema) =>
        schema.required(
          i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })
        ),
      otherwise: (schema) => schema.optional(),
    }),
});

export type ActionURLSchemaTypeTramit = yup.InferType<typeof ActionURLSchema>;

export const ActionURLVideoSchema = yup.object({
  routeType: yup
    .mixed<RouterType>()
    .oneOf(Object.values(RouterType))
    .default(RouterType.ExternalRoute)
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  url: yup.string().when('routeType', {
    is: (routeType: RouterType) => routeType === RouterType.InternaltRoute,
    then: (schema) => schema.optional().nullable(),
    otherwise: (schema) =>
      schema
        .required(i18n._({ id: 'commons.form.required.field', message: 'Campo requerido' }))
        .when('routeType', {
          is: RouterType.ExternalRoute, // Utilizar la constante directamente
          then: (schema) => schema.matches(REGEX_URL, 'Debe ingresar una URL válida'),
        })
        .when('routeType', {
          is: RouterType.InternalRouteWithArguments, // Utilizar la constante directamente
          then: (schema) => schema.optional().nullable(),
        }),
  }),
  target: yup
    .string()
    .oneOf(['_blank', '_self'])
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  arguments: yup.array(yup.string()).optional(),
  routeNavigation: yup
    .string()
    .default(SubPages.Transparency)
    .when('routeType', {
      is: (routeType: RouterType) => routeType === RouterType.InternaltRoute,
      then: (schema) =>
        schema.required(
          i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })
        ),
      otherwise: (schema) => schema.optional(),
    }),
});

export type ActionURLVideoSchemaType = yup.InferType<typeof ActionURLVideoSchema>;

export const TabSocialNetworkSchema = yup
  .object({
    uuid: yup.string(),
    title: yup
      .string()
      .required(i18n._({ id: 'commons.form.required.field', message: 'Campo requerido' })),
  })
  .concat(ActionURLSchema);

export type TabSocialNetworkSchemaType = yup.InferType<typeof TabSocialNetworkSchema>;

export const CarouselSocialNetworkSchema = yup
  .object({
    uuid: yup.string(),
    title: yup
      .string()
      .required(i18n._({ id: 'commons.form.required.field', message: 'Campo requerido' })),
    alternativeText: yup.string(),
    image: yup
      .array()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' }))
      .min(1, 'Debe cargar una imagen')
      .of(
        yup
          .object()
          .shape({ id: yup.string().required() })
          .required('Imagen requerida')
          .typeError('Documento inválido')
      ),
  })
  .concat(ActionURLSchema);

export type CarouselSocialNetworkSchemaType = yup.InferType<typeof CarouselSocialNetworkSchema>;

export const RadioInputTheme = yup.object({
  theme: yup.string().required('Debe seleccionar un tema'),
});

export type RadioInputThemeType = yup.InferType<typeof RadioInputTheme>;

export const NewItemLogo = yup

  .object({
    alternativetext: yup
      .string()
      .required(
        i18n._({
          id: 'commons.form.required.field.new.alternativetext.newitem.newww',
          message: 'Campo requerido',
        })
      )
      .max(38, 'El texto alternativo no puede tener más de 38 caracteres'),
    image: yup
      .array()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' }))
      .min(1, 'Debe cargar una imagen')
      .of(
        yup
          .object()
          .shape({ id: yup.string().required() })
          .required('Imagen requerida')
          .typeError('Documento inválido')
      ),

    corporateLogoName: yup.string().when('style', {
      is: (style: any) => style === 'A',
      then: (schema) =>
        schema.max(1000, 'El cuerpo de texto no puede tener más de 1000 caracteres'),
      otherwise: (schema) => schema.required('Campo requerido'),
    }),
  })
  .concat(ActionURLSchema);

export const NewItemCarouselInstitutionalProgram = yup

  .object({
    style: yup.string(),
    // image: yup
    //   .mixed()
    //   .test("file", "Campo requerido", (value: any) => {
    //     return value && value.length > 0;
    //   })
    //   .required(
    //     i18n._({
    //       id: "commons.form.required.field.new.file",
    //       message: "Campo requerido",
    //     })
    //   ),
    alternativeText: yup
      .string()
      .required(
        i18n._({
          id: 'commons.form.required.field.new.alternativetext.newitem.newww',
          message: 'Campo requerido',
        })
      )
      .max(38, 'El texto alternativo no puede tener más de 38 caracteres'),
    image: yup
      .array()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' }))
      .min(1, 'Debe cargar una imagen')
      .of(
        yup
          .object()
          .shape({ id: yup.string().required() })
          .required('Imagen requerida')
          .typeError('Documento inválido')
      ),
  })
  .concat(ActionURLSchema);

export type NewItemCarouselInstitutionalProgram = yup.InferType<
  typeof NewItemCarouselInstitutionalProgram
>;

export const NewItemCarousel = yup

  .object({
    style: yup.string(),

    alternativeText: yup
      .string()
      .required(
        i18n._({
          id: 'commons.form.required.field.new.alternativetext.newitem.newww',
          message: 'Campo requerido',
        })
      )
      .max(38, 'El texto alternativo no puede tener más de 38 caracteres'),
    image: yup
      .array()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' }))
      .min(
        1,
        i18n._({ id: 'commons.validation.must.upload.image', message: 'Debe cargar una imagen' })
      )
      .of(
        yup
          .object()
          .shape({ id: yup.string().required() })
          .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' }))
      )
      .typeError('Debe cargar una imagen'),
    description: yup.string().when('style', {
      is: (style: any) => style === 'A',
      then: (schema) =>
        schema.max(1000, 'El cuerpo de texto no puede tener más de 1000 caracteres'),
      otherwise: (schema) => schema.required('Campo requerido'),
    }),

    title: yup.string().when('style', {
      is: (style: any) => style === 'A',
      then: (schema) =>
        schema.max(1000, 'El cuerpo de texto no puede tener más de 1000 caracteres'),
      otherwise: (schema) => schema.required('Campo requerido'),
    }),
  })
  .concat(ActionURLSchema);

export type NewItemCarousel = yup.InferType<typeof NewItemCarousel>;

export const EditItemCarousel = yup

  .object({
    style: yup.string(),
    image: yup
      .array()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' }))
      .min(
        1,
        i18n._({ id: 'commons.validation.must.upload.image', message: 'Debe cargar una imagen' })
      )
      .of(
        yup
          .object()
          .shape({ id: yup.string().required() })
          .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' }))
      )
      .typeError('Debe cargar una imagen'),
    description: yup.string().when('style', {
      is: (style: any) => style === 'A',
      then: (schema) =>
        schema.max(1000, 'El cuerpo de texto no puede tener más de 1000 caracteres'),
      otherwise: (schema) => schema.required('Campo requerido'),
    }),

    title: yup.string().when('style', {
      is: (style: any) => style === 'A',
      then: (schema) =>
        schema.max(1000, 'El cuerpo de texto no puede tener más de 1000 caracteres'),
      otherwise: (schema) => schema.required('Campo requerido'),
    }),

    alternativeText: yup
      .string()
      .required(
        i18n._({
          id: 'commons.form.required.field.alternative.textedit',
          message: 'Campo requerido',
        })
      )
      .max(38, 'El texto alternativo no puede tener más de 38 caracteres'),
  })
  .concat(ActionURLSchema);

export type EditItemCarousel = yup.InferType<typeof EditItemCarousel>;

export const SchemaBlockStyle = yup.object({
  style: yup
    .mixed<PageBlockStyles>()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  descriptor: yup.string().optional(),
  blocktitle: yup.string().optional(),
  publicDescription: yup.string().optional(),
  showTitle: yup.boolean().optional(),
  showDescription: yup.boolean().optional(),
  isCarrousel: yup.boolean().optional(),
  color: yup.string().optional(),
  itemsPerView: yup.number().optional(),
});

export type SchemaBlockStyleType = yup.InferType<typeof SchemaBlockStyle>;

export const SchemaStyleTitleDescription = yup.object({
  style: yup
    .mixed<PageBlockStyles>()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  title: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  descriptor: yup.string().optional(),
  description: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  publicDescription: yup.string().optional(),
  showTitle: yup.boolean().optional(),
  showDescription: yup.boolean().optional(),
  placeholder: yup.string().optional(),
  introduction: yup.string().optional(),
  searchbar: yup.boolean().default(false),
  global: yup.boolean().default(false),
  hasSearchbar: yup.boolean().default(false),
  dateVisibility: yup.boolean().optional().nullable(),
});

export type SchemaStyleTitleDescriptionType = yup.InferType<typeof SchemaStyleTitleDescription>;

// Schema específico para SliderNews con campos adicionales
export const SchemaSliderNews = SchemaStyleTitleDescription.shape({
  hasDependencyFilter: yup.boolean().default(false),
  includeSharedContent: yup.boolean().default(false),
  contentSchemaId: yup.string().optional().nullable(),
});

export type SchemaSliderNewsType = yup.InferType<typeof SchemaSliderNews>;

export type SchemaStyledTableType = yup.InferType<typeof SchemaStyledTable>;

export const SchemaStyledTable = yup.object({
  style: yup.string(),
  title: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  descriptor: yup.string().optional(),
  description: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  publicDescription: yup.string().optional(),
  showTitle: yup.boolean().optional(),
  showDescription: yup.boolean().optional(),
  typeContent: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  content: yup.string(),
  typeSchema: yup.string(),
  itemsPerPage: yup
    .number()
    .optional()
    .nullable()
    .transform((value, originalValue) => {
      if (originalValue === '' || originalValue === null || originalValue === undefined) {
        return undefined;
      }
      if (value === 0) {
        return undefined;
      }
      return value;
    })
    .typeError('Debe ingresar un valor numérico'),
});

export const SchemaImage = yup.object({
  image: yup
    .array()
    .typeError('Documento requerido')
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' }))
    .min(1, 'Debe cargar una imagen')
    .of(
      yup
        .object()
        .typeError('Documento no válido')
        .shape({ id: yup.string().required() })
        .required('Imagen requerida')
        .typeError('Documento inválido')
    ),
});

export type SchemaImageType = yup.InferType<typeof SchemaImage>;

export const SchemaOptionalImage = yup.object({
  image: yup.array().of(yup.object().shape({ id: yup.string().required() })),
});

export type SchemaOptionalImageType = yup.InferType<typeof SchemaOptionalImage>;

export const SchemaFile = yup.object({
  file: yup
    .array()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' }))
    .min(1, 'Debe cargar un documento')
    .of(
      yup
        .object()
        .shape({ id: yup.string().required() })
        .required('Documento requerido')
        .typeError('Documento inválido')
    ),
});

export type SchemaFileType = yup.InferType<typeof SchemaFile>;

export const SchemaFileOptional = yup.object({
  file: yup
    .array()
    .of(
      yup
        .object()
        .shape({ id: yup.string().required() })
        .required('Documento requerido')
        .typeError('Documento inválido')
    )
    .optional()
    .nullable(),
});

export type SchemaFileOptionalType = yup.InferType<typeof SchemaFileOptional>;

export const TopBarInputs = yup.object({
  hasSigninName: yup
    .string()
    .required(
      i18n._({ id: 'commons.form.required.field.alternative.textedit', message: 'Campo requerido' })
    ),
  hasGovcoLogo: yup.boolean(),
  hasSignupName: yup
    .string()
    .required(
      i18n._({ id: 'commons.form.required.field.alternative.textedit', message: 'Campo requerido' })
    ),
  routeType: yup
    .mixed<RouterType>()
    .oneOf(Object.values(RouterType))
    .default(RouterType.ExternalRoute)
    .when('hasGovcoLogo', {
      is: (hasGovcoLogo: boolean) => hasGovcoLogo === true,
      then: (schema) =>
        schema.required(
          i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
  url: yup.string().when('hasGovcoLogo', {
    is: (hasGovcoLogo: boolean) => hasGovcoLogo === true,
    then: (schema) =>
      schema.when('routeType', {
        is: (routeType: RouterType) => routeType === RouterType.InternaltRoute,
        then: (schema) => schema.optional(),
        otherwise: (schema) =>
          schema
            .required(i18n._({ id: 'commons.form.required.field', message: 'Campo requerido' }))
            .when('routeType', {
              is: RouterType.ExternalRoute, // Utilizar la constante directamente
              then: (schema) => schema.matches(REGEX_URL, 'Debe ingresar una URL válida'),
            })
            .when('routeType', {
              is: RouterType.InternalRouteWithArguments, // Utilizar la constante directamente
              then: (schema) => schema.optional(),
            }),
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
  target: yup
    .string()
    .oneOf(['_blank', '_self'])
    .when('hasGovcoLogo', {
      is: (hasGovcoLogo: boolean) => hasGovcoLogo === true,
      then: (schema) =>
        schema.required(
          i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
  arguments: yup.array(yup.string()).optional(),

  routeNavigation: yup
    .string()
    .default(SubPages.Transparency)
    .when('hasGovcoLogo', {
      is: (hasGovcoLogo: boolean) => hasGovcoLogo === true,
      then: (schema) =>
        schema.when('routeType', {
          is: (routeType: RouterType) => routeType === RouterType.InternaltRoute,
          then: (schema) =>
            schema.required(
              i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })
            ),
          otherwise: (schema) => schema.optional(),
        }),
      otherwise: (schema) => schema.notRequired(),
    }),
});

export const SchemaQuickAccessBar = yup
  .object({
    title: yup
      .string()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
    alternativeText: yup.string(),
    description: yup.string(),
  })
  .concat(SchemaOptionalImage)
  .concat(ActionURLSchema);

export type SchemaQuickAccessBarType = yup.InferType<typeof SchemaQuickAccessBar>;

export const SchemaTaxAccessButton = yup
  .object({
    title: yup
      .string()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
    description: yup
      .string()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
    alternativeText: yup.string(),
  })
  .concat(SchemaImage)
  .concat(ActionURLSchema);

export type SchemaTaxAccessButtonType = yup.InferType<typeof SchemaTaxAccessButton>;

export const SchemaNavigationBarOnlyTitle = yup.object({
  title: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
});

export type SchemaNavigationBarOnlyTitleType = yup.InferType<typeof SchemaNavigationBarOnlyTitle>;

export const SchemaNavigationBar = yup
  .object({
    title: yup
      .string()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  })
  .concat(ActionURLSchema);

export type SchemaNavigationBarType = yup.InferType<typeof SchemaNavigationBar>;

// Nuevo esquema derivado: solo valida el título y NO concatena ActionURLSchema.
// Útil para casos donde no se desea validar campos de acción (por ejemplo, elementos de tipo Seccion).
export const SchemaNavigationBarNoAction = yup.object({
  title: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
});

export type SchemaNavigationBarNoActionType = yup.InferType<typeof SchemaNavigationBarNoAction>;

export const NewSchemaNavigationBar = yup
  .object({
    // iconId: yup.string().required('El icono es requerido'),
    iconId: imageType,
    gifId: imageType,
  })
  .concat(SchemaNavigationBar);

export type NewSchemaNavigationBarType = yup.InferType<typeof NewSchemaNavigationBar>;

export const SchemaRecoveryPassword = yup.object({
  email: yup
    .string()
    .email('Debe proporcionar un correo válido')
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
});

export type SchemaRecoveryPasswordType = yup.InferType<typeof SchemaRecoveryPassword>;

export const SchemaCodeVerification = yup.object({
  code: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
});

export type SchemaCodeVerificationType = yup.InferType<typeof SchemaCodeVerification>;

export const SchemaUpdatePasswordRecovery = yup.object({
  newPassword: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' }))
    .matches(
      REGEX_PASSWORD,
      i18n._({ id: 'commons.message.invalidad.password', message: 'Contraseña no válida' })
    ),
  newPasswordConfirm: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' }))
    .oneOf(
      [yup.ref('newPassword')],
      i18n._({ id: 'commons.message.password.not.match', message: 'Las contraseñas no coinciden' })
    ),
});

export type SchemaUpdatePasswordRecoveryType = yup.InferType<typeof SchemaUpdatePasswordRecovery>;

export const SchemaTransparencyAccordion = yup
  .object({
    title: yup
      .string()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
    index: yup
      .string()
      .matches(REGEX_NUMBER, 'Debe proporcionar un número')
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  })
  .concat(ActionURLSchema);

export type SchemaTransparencyAccordionType = yup.InferType<typeof SchemaTransparencyAccordion>;

export const SchemaTransparencyIntroduction = yup.object({
  descriptor: yup.string().optional(),
  content: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  hasVideo: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  link: yup.object().when('hasVideo', {
    is: (hasVideo: string) => hasVideo === '1',
    then: (schema) => schema.concat(ActionURLSchema),
    otherwise: (schema) => schema.concat(SchemaImage),
  }),
  items: yup.array().of(
    yup
      .object()
      .shape({
        uuid: yup.string(),
        title: yup
          .string()
          .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
        description: yup
          .string()
          .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
      })
      .concat(ActionURLSchema)
      .concat(SchemaImage)
  ),
});

export type SchemaTransparencyIntroductionType = yup.InferType<
  typeof SchemaTransparencyIntroduction
>;

export const SchemaBlockPressRoomNewsletter = yup
  .object({
    style_tags: yup
      .mixed<PageBlockStyles>()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  })
  .concat(SchemaBlockStyle);

export type SchemaBlockPressRoomNewsletterType = yup.InferType<
  typeof SchemaBlockPressRoomNewsletter
>;

export const SchemaBlockParticipateHeader = yup
  .object({
    introduction: yup.string().optional(),
    searchbar: yup.boolean().optional(),
    placeholder: yup.string().optional(),
  })
  .concat(SchemaStyleTitleDescription);

export type SchemaBlockParticipateHeaderType = yup.InferType<typeof SchemaBlockParticipateHeader>;

////////////////////////////////////////
export const SchemaBlockRegulationHeader = yup.object({
  style: yup
    .mixed<PageBlockStyles>()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  titlesubheader: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  descriptionsubheader: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
});

export type SchemaBlockRegulationHeaderType = yup.InferType<typeof SchemaBlockRegulationHeader>;
/////////////////////////////////////

export const SchemaBlockRegulationHeaderAccordion = yup.object({
  style: yup
    .mixed<PageBlockStyles>()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
});

export type SchemaBlockRegulationHeaderTypeAccordion = yup.InferType<
  typeof SchemaBlockRegulationHeaderAccordion
>;

export const SchemaBlockInstitutionalProgramsHeader = yup
  .object({
    introduction: yup.string().optional(),
    searchbar: yup.boolean().optional(),
    placeholder: yup.string().optional(),
  })
  .concat(SchemaStyleTitleDescription);

export type SchemaBlockInstitutionalProgramsHeaderType = yup.InferType<
  typeof SchemaBlockInstitutionalProgramsHeader
>;

export const SchemaBlockRegulationsHeader = yup
  .object({
    introduction: yup.string().optional(),
    searchbar: yup.boolean().optional(),
    placeholder: yup.string().optional(),
  })
  .concat(SchemaStyleTitleDescription);

export type SchemaBlockRegulationsHeaderType = yup.InferType<typeof SchemaBlockRegulationsHeader>;

export const SchemaBlockPqrsdHeader = yup
  .object({
    introduction: yup.string().optional(),
    searchbar: yup.boolean().optional(),
    placeholder: yup.string().optional(),
  })
  .concat(SchemaStyleTitleDescription);

export type SchemaBlockPqrsdHeaderType = yup.InferType<typeof SchemaBlockPqrsdHeader>;

export const SchemaBlockDynamicsHeader = yup
  .object({
    introduction: yup.string().optional(),
    searchbar: yup.boolean().optional(),
    placeholder: yup.string().optional(),
  })
  .concat(SchemaStyleTitleDescription);

export type SchemaBlockDynamicsHeaderType = yup.InferType<typeof SchemaBlockDynamicsHeader>;

export const SchemaSocialNetworkPressRoom = yup
  .object({
    title: yup
      .string()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
    socialNetworkType: yup.mixed<SocialNetworkType>().required(),
  })
  .concat(ActionURLSchema);

export type SchemaSocialNetworkPressRoomType = yup.InferType<typeof SchemaSocialNetworkPressRoom>;

export const SchemaPageParticipateElement = yup
  .object({
    title: yup
      .string()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
    description: yup
      .string()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
    alternativeText: yup.string(),
  })
  .concat(SchemaImage)
  .concat(ActionURLSchema);

export type SchemaPageParticipateElementType = yup.InferType<typeof SchemaPageParticipateElement>;

export const SchemaContentRegulation = yup
  .object({
    title: yup
      .string()
      .max(250, 'El nombre no puede tener más de 250 caracteres')
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),

    regulationtypes: yup
      .string()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' }))
      .notOneOf([''], 'Debe seleccionar un tipo de filtro'),

    dependencyId: yup
      .string()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' }))
      .notOneOf([''], 'Debe seleccionar una dependencia'),

    expeditionDate: yup
      .string()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),

    publishDate: yup
      .string()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),

    description: yup
      .string()
      .max(2000, 'La descripción no puede tener más de 2000 caracteres')
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
    downloadText: yup.string(),
    seoInput: yup.object({
      metadata: yup
        .array()
        .of(seoMetadataInputFormSchema)
        .min(1, t`Se requiere al menos un conjunto de metadatos`),
    }),
  })
  .concat(SchemaOptionalImage)
  .concat(SchemaFile);

export type SchemaContentRegulationType = yup.InferType<typeof SchemaContentRegulation>;

export const SchemaTabSocialNetwork = yup
  .object({
    title: yup
      .string()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
    socialNetworkType: yup.mixed<SocialNetworkType>().required(),
  })
  .concat(ActionURLSchema);

export type SchemaTabSocialNetworkType = yup.InferType<typeof SchemaTabSocialNetwork>;

export const SchemaCarouselSocialNetwork = yup
  .object({
    title: yup
      .string()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
    alternativeText: yup.string().nullable().optional(),
  })
  .concat(ActionURLSchema)
  .concat(SchemaImage);

export type SchemaCarouselSocialNetworkType = yup.InferType<typeof SchemaCarouselSocialNetwork>;

export const imageTypeOptional = yup
  .array()
  .of(
    yup.object().shape({ id: yup.string().required() }).optional()
    // .typeError('Documento inválido')
  )
  // .typeError('Documento inválido')
  .optional()
  // add a validation to make sure that the if the value is an object return null
  .transform((value, originalValue) => {
    if (typeof originalValue === 'object' && Object.keys(originalValue).length === 0) {
      return undefined;
    }
    return value;
  })
  .default(undefined);

export const actionUrlType = yup.object({
  routeType: yup
    .mixed<RouterType>()
    .oneOf(Object.values(RouterType))
    .default(RouterType.ExternalRoute)
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  url: yup.string().when('routeType', {
    is: (routeType: RouterType) => routeType === RouterType.InternaltRoute,
    then: (schema) => schema.optional().nullable(),
    otherwise: (schema) =>
      schema
        .required(i18n._({ id: 'commons.form.required.field', message: 'Campo requerido' }))
        .when('routeType', {
          is: RouterType.ExternalRoute, // Utilizar la constante directamente
          then: (schema) => schema.matches(REGEX_URL, 'Debe ingresar una URL válida'),
        })
        .when('routeType', {
          is: RouterType.InternalRouteWithArguments, // Utilizar la constante directamente
          then: (schema) => schema.optional().nullable(),
        }),
  }),
  target: yup
    .string()
    .oneOf(['_blank', '_self'])
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  arguments: yup.array(yup.string()).optional(),
  routeNavigation: yup
    .string()
    .default(SubPages.Transparency)
    .when('routeType', {
      is: (routeType: RouterType) => routeType === RouterType.InternaltRoute,
      then: (schema) =>
        schema.required(
          i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })
        ),
      otherwise: (schema) => schema.optional(),
    }),
});

export const actionUrlTypeOptional = yup.object({
  routeType: yup
    .mixed<RouterType>()
    .oneOf(Object.values(RouterType))
    .default(RouterType.ExternalRoute),
  // .required(
  //   i18n._({
  //     id: 'commons.message.required.field',
  //     message: 'Campo requerido',
  //   })
  // )
  url: yup.string().when('routeType', {
    is: (routeType: RouterType) =>
      routeType === RouterType.InternaltRoute || routeType === RouterType.NoAction,
    then: (schema) => schema.optional().nullable(),
    otherwise: (schema) =>
      schema
        // .required(i18n._({ id: 'commons.form.required.field', message: 'Campo requerido' }))
        .optional()
        .when('routeType', {
          is: RouterType.ExternalRoute, // Utilizar la constante directamente
          then: (schema) =>
            schema.matches(REGEX_URL, {
              excludeEmptyString: true,
              message: 'Debe ingresar una URL válida',
            }),
        })
        .when('routeType', {
          is: RouterType.InternalRouteWithArguments, // Utilizar la constante directamente
          then: (schema) => schema.optional().nullable(),
        }),
  }),
  target: yup.string().oneOf(['_blank', '_self']).optional(),
  // .required(
  //   i18n._({
  //     id: 'commons.message.required.field',
  //     message: 'Campo requerido',
  //   })
  // )
  arguments: yup.array(yup.string()).optional().nullable(),
  routeNavigation: yup
    .string()
    .nullable()
    .when('routeType', {
      is: (routeType: RouterType) => routeType === RouterType.InternaltRoute,
      then: (schema) =>
        schema.required(
          i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })
        ),
      otherwise: (schema) => schema.optional().nullable(),
    }),
});

export const SchemaOrganizationHeader = yup.object({
  title: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  subtitle: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  image: imageType,
  linkImage: actionUrlType,
  imageAdmin: imageTypeOptional,
  linkImageAdmin: actionUrlTypeOptional,
  imageOther: imageTypeOptional,
  linkImageOther: actionUrlTypeOptional,
});

export type SchemaOrganizationHeaderType = yup.InferType<typeof SchemaOrganizationHeader>;

export const SchemaSearchBlock = yup.object({
  description: yup.string().required('Campo requerido'),
  placeholder: yup.string().required('Campo requerido'),
  style: yup
    .mixed<PageBlockStyles>()
    .oneOf(Object.values(PageBlockStyles))
    .default(PageBlockStyles.A)
    .required('Campo requerido'),
});

export type SchemaSearchBlockType = yup.InferType<typeof SchemaSearchBlock>;

export const SchemaParticipateContent = yup.object({
  title: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  summary: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  content: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
});

export type SchemaParticipateContentType = yup.InferType<typeof SchemaParticipateContent>;

export const SchemaParameter = yup
  .object({
    code: yup
      .string()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
    description: yup
      .string()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
    type: yup
      .mixed<TypeParameter>()
      .oneOf(Object.values(TypeParameter))
      .default(TypeParameter.Character)
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
    character: yup
      .string()
      .nullable()
      .optional()
      .when('type', {
        is: (type: TypeParameter) => type === TypeParameter.Character,
        then: (schema) =>
          schema.required(
            i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })
          ),
        otherwise: (schema) => schema.optional().nullable(),
      }),
    number: yup
      .number()
      .nullable()
      .optional()
      .typeError('Número no válido')
      .when('type', {
        is: (type: TypeParameter) => type === TypeParameter.Number,
        then: (schema) =>
          schema.required(
            i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })
          ),
        otherwise: (schema) => schema.optional().nullable(),
      }),
    boolean: yup
      .boolean()
      .nullable()
      .optional()
      .when('type', {
        is: (type: TypeParameter) => type === TypeParameter.Boolean,
        then: (schema) =>
          schema.required(
            i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })
          ),
        otherwise: (schema) => schema.optional().nullable(),
      }),
    json: yup
      .string()
      .nullable()
      .optional()
      .when('type', {
        is: (type: TypeParameter) => type === TypeParameter.Json,
        then: (schema) =>
          schema.required(
            i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })
          ),
        otherwise: (schema) => schema.optional().nullable(),
      }),
    date: yup
      .string()
      .nullable()
      .optional()
      .when('type', {
        is: (type: TypeParameter) => type === TypeParameter.Date,
        then: (schema) =>
          schema.required(
            i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })
          ),
        otherwise: (schema) => schema.optional().nullable(),
      }),
  })
  .concat(SchemaFileOptional);

export type SchemaParameterType = yup.InferType<typeof SchemaParameter>;

export const SchemaDynamicPage = yup.object({
  name: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  dependencyId: yup.string().required('Campo requerido'),
  description: yup.string().required('Campo requerido'),

  seoInput: yup.object({
    metadata: yup
      .array()
      .of(seoMetadataInputFormSchema)
      .min(1, t`Se requiere al menos un conjunto de metadatos`),
  }),

  // content: yup.string().required(
  //   i18n._({
  //     id: 'commons.message.required.field',
  //     message: 'Campo requerido',
  //   })
  // ),
  // section: yup.mixed<DynamicPageSection>().required(
  //   i18n._({
  //     id: 'commons.message.required.field',
  //     message: 'Campo requerido',
  //   })
  // ),
});

export type SchemaDynamicPageType = yup.InferType<typeof SchemaDynamicPage>;

export const SchemaCustomBlock = yup.object({
  blockTitle: yup.string().optional(),
  descriptor: yup.string().optional(),
  publicDescription: yup.string().optional(),
  showTitle: yup.boolean().optional(),
  showDescription: yup.boolean().optional(),
  content: yup
    .string()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
});

export type SchemaCustomBlockType = yup.InferType<typeof SchemaCustomBlock>;

export const SchemaSubHeader = yup.object({
  style: yup
    .mixed<PageBlockStyles>()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),

  hassearchbar: yup.boolean(),
  placeholdersubheader: yup
    .string()
    .required(i18n._({ id: 'admin.user.form.name.requerido', message: 'Campo requerido' })),
  titlesubheader: yup
    .string()
    .required(i18n._({ id: 'admin.user.form.name.requerido', message: 'Campo requerido' })),
  descriptionsubheader: yup
    .string()
    .required(i18n._({ id: 'admin.user.form.name.requerido', message: 'Campo requerido' })),
});

export type SchemaSubHeaderType = yup.InferType<typeof SchemaSubHeader>;
//////////////////////////////

export const SchemaSubHeaderRegulations = yup.object({
  style: yup
    .mixed<PageBlockStyles>()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),

  hassearchbar: yup.boolean(),
  placeholdersubheader: yup
    .string()
    .required(i18n._({ id: 'admin.user.form.name.requerido', message: 'Campo requerido' })),
  titlesubheader: yup
    .string()
    .required(i18n._({ id: 'admin.user.form.name.requerido', message: 'Campo requerido' })),
  descriptionsubheader: yup
    .string()
    .required(i18n._({ id: 'admin.user.form.name.requerido', message: 'Campo requerido' })),
});

export type SchemaSubHeaderTypeRegu = yup.InferType<typeof SchemaSubHeaderRegulations>;

/////////////////

export const SchemaCopySliderItems = yup.object({
  style_from: yup
    .mixed<PageBlockStyles>()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  style_to: yup
    .mixed<PageBlockStyles>()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
});

export type SchemaCopySliderItemsType = yup.InferType<typeof SchemaCopySliderItems>;

type CreatePqrsBlockType =
  | 'subHeaderBlock'
  | 'pqrsdfActionBlock'
  | 'pqrsdfConsultBlock'
  | 'newsletterBlock'
  | 'subDownloadBlock'
  | 'regulationsBlock';
export const SchemaCreateBlock = yup.object({
  block: yup
    .mixed<CreatePqrsBlockType>()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  // block_name: yup
  //   .string()
  //   .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
});

export type SchemaCreateBlockType = yup.InferType<typeof SchemaCreateBlock>;

export const SchemaPqrsdActionContent = yup
  .object({
    name: yup
      .string()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
    description: yup
      .string()
      .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
    processDefinitionId: yup
      .string()
      .optional()
      .max(60, 'El ID del workflow no puede tener más de 60 caracteres'),
    icon: imageType,
  })
  .concat(ActionURLSchema);

export type SchemaPqrsdActionContentType = yup.InferType<typeof SchemaPqrsdActionContent>;

export const ExampleSchema = yup.object({
  style: yup
    .mixed<PageBlockStyles>()
    .required(i18n._({ id: 'commons.message.required.field', message: 'Campo requerido' })),
  hideFirstName: yup.mixed<PageBlockStyles>().when('style', ([style], schema) => {
    return schema.default(style);
  }),
});

export type ExampleSchemaType = yup.InferType<typeof ExampleSchema>;

export const DynamicListSchema = yup.object({
  name: yup
    .string()
    .required('Campo requerido')
    .max(200, 'El nombre no puede tener más de 200 caracteres'),
  description: yup
    .string()
    .required('Campo requerido')
    .max(200, 'El nombre no puede tener más de 200 caracteres'),
  dependencyId: yup.string().required('Campo requerido'),
  allowChildren: yup.boolean().default(false),
});

export type DynamicListSchemaType = yup.InferType<typeof DynamicListSchema>;

export const DocumentTypeSchema = yup
  .object({
    fields: yup
      .array()
      .of(
        yup.object().shape({
          id: yup.string().required('Campo requerido'),
          orderIndex: yup.number().required('Campo requerido'),
          fieldName: yup
            .string()
            .required('Campo requerido')
            .max(50, 'El nombre no puede tener más de 50 caracteres'),
          fieldType: yup.string().required('Campo requerido'),
          fieldLabel: yup
            .string()
            .required('Campo requerido')
            .max(50, 'El nombre no puede tener más de 50 caracteres'),
          isRequired: yup.boolean().required('Campo requerido'),
          isGroup: yup.boolean().required('Campo requerido'),
          createdAt: yup.string().required('Campo requerido'),
          isActive: yup.boolean().required('Campo requerido'),
        })
      )
      .min(1, 'Debe agregar al menos un campo'),
  })
  .concat(DynamicListSchema);

export type DocumentTypeSchemaType = yup.InferType<typeof DocumentTypeSchema>;

export const DocumentSchema = yup
  .object({
    name: yup
      .string()
      .required('Campo requerido')
      .max(50, 'El nombre no puede tener más de 50 caracteres'),
    description: yup
      .string()
      .required('Campo requerido')
      .max(200, 'La descripción no puede tener más de 200 caracteres'),
    dependencyId: yup.string().required('Campo requerido'),
    contentDate: yup.string().required('Campo requerido'),
    publishDate: yup.string().required('Campo requerido'),
    metadata: yup.array().of(
      yup.object().shape({
        id: yup.string().optional(),
        schemaFieldId: yup.string().required('Campo requerido'),
        value: yup.string().required('Campo requerido'),
      })
    ),
  })
  .concat(SchemaFile);

// Función para generar esquema dinámico basado en los campos del ContentSchema
export const createDocumentSchema = (documentFields: ContentSchemaField[]) => {
  const metadataSchema = yup.array().of(
    yup.object().shape({
      id: yup.string().optional(),
      schemaFieldId: yup.string().required('Campo requerido'),
      value: yup.lazy((value, { parent }) => {
        // Encontrar el campo correspondiente por schemaFieldId
        const field = documentFields.find((f) => f.id === parent.schemaFieldId);

        if (!field) return yup.string().optional();

        // Base schema
        let schema = yup.string();

        // Aplicar validación de tipos
        if (field.fieldType === FieldType.Email)
          schema = schema.email('Debe ingresar un email válido');

        // Si el campo es requerido, hacer el valor requerido
        if (field.isRequired) return schema.required(`${field.fieldLabel} es requerido`);

        // Si no es requerido, hacerlo opcional
        return schema.optional();
      }),
    })
  );

  return yup
    .object({
      name: yup
        .string()
        .required('Campo requerido')
        .max(250, 'El nombre no puede tener más de 250 caracteres'),
      dependencyId: yup.string().required('Campo requerido'),
      description: yup
        .string()
        .required('Campo requerido')
        .max(2000, 'La descripción no puede tener más de 2000 caracteres'),
      contentDate: yup
        .string()
        .required('Campo requerido')
        .test('is-valid-date', 'Debe ingresar una fecha válida', (value) => {
          if (!value) return false;
          // Check if it's not an invalid date string
          if (value === 'Fecha inválida' || value === 'Invalid date') return false;
          // Check if it's a valid date format (YYYY-MM-DD)
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (!dateRegex.test(value)) return false;
          // Check if the date is actually valid
          const date = new Date(value);
          return date instanceof Date && !isNaN(date.getTime());
        }),
      publishDate: yup
        .string()
        .required('Campo requerido')
        .test('is-valid-date', 'Debe ingresar una fecha válida', (value) => {
          if (!value) return false;
          // Check if it's not an invalid date string
          if (value === 'Fecha inválida' || value === 'Invalid date') return false;
          // Check if it's a valid date format (YYYY-MM-DD)
          const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
          if (!dateRegex.test(value)) return false;
          // Check if the date is actually valid
          const date = new Date(value);
          return date instanceof Date && !isNaN(date.getTime());
        }),
      metadata: metadataSchema,
      seoInput: yup.object({
        metadata: yup
          .array()
          .of(seoMetadataInputFormSchema)
          .min(1, t`Se requiere al menos un conjunto de metadatos`),
      }),
    })
    .concat(SchemaFile);
};

export type DocumentSchemaType = yup.InferType<typeof DocumentSchema>;

export const AlertBlockSchema = yup.object({
  alertStyle: yup.mixed<PageBlockStyles>().required('Campo requerido'),
  title: yup
    .string()
    .max(200, 'El título no puede tener más de 200 caracteres')
    .when('alertStyle', {
      is: (alertStyle: PageBlockStyles) => alertStyle === PageBlockStyles.B,
      then: (schema) => schema.required('Campo requerido'),
      otherwise: (schema) => schema.optional().nullable(),
    }),
  descriptor: yup.string().optional(),
  description: yup
    .string()
    .max(200, 'La descripción no puede tener más de 200 caracteres')
    .when('alertStyle', {
      is: (alertStyle: PageBlockStyles) => alertStyle === PageBlockStyles.B,
      then: (schema) => schema.required('Campo requerido'),
      otherwise: (schema) => schema.optional().nullable(),
    }),
  showTitle: yup.boolean().optional(),
  showDescription: yup.boolean().optional(),
  frequency: yup.string().required('Campo requerido'),
  contentType: yup
    .string()
    .oneOf(['image', 'html'], 'Tipo de contenido inválido')
    .required('Campo requerido'),
  file: yup
    .array()
    .of(
      yup
        .object()
        .shape({ id: yup.string().required() })
        .required('Documento requerido')
        .typeError('Documento inválido')
    )
    .when('contentType', {
      is: 'image',
      then: (schema) => schema.required('Campo requerido').min(1, 'Debe cargar una imagen'),
      otherwise: (schema) => schema.optional().nullable(),
    }),
  htmlTemplate: yup.string().when('contentType', {
    is: 'html',
    then: (schema) =>
      schema
        .required('Campo requerido')
        .test('not-empty-html', 'El contenido HTML es requerido', (value) => {
          return Boolean(value && value.trim() !== '' && value.trim() !== '<p><br></p>');
        }),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  width: yup.string().when('contentType', {
    is: 'html',
    then: (schema) => schema.required('Campo requerido'),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  height: yup.string().when('contentType', {
    is: 'html',
    then: (schema) => schema.required('Campo requerido'),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  modalSizeUnit: yup.string().when('contentType', {
    is: 'html',
    then: (schema) => schema.required('Campo requerido'),
    otherwise: (schema) => schema.optional().nullable(),
  }),
  buttonTitle: yup.string().optional().nullable(),
  linkItem: actionUrlTypeOptional,
});

export type AlertBlockSchemaType = yup.InferType<typeof AlertBlockSchema>;

export const SchemaAccessibilityButtonItem = yup
  .object({
    linkName: yup.string().required('Campo requerido'),
    iconType: yup.mixed<AccessibilityIconType>().required(),
  })
  .concat(ActionURLSchema);

export type SchemaAccessibilityButtonItemType = yup.InferType<typeof SchemaAccessibilityButtonItem>;
